<!--
  Preloading strategy:
    - preference for KISSed unique small spinner for breadcrumber only
      rather than preloader placeholders
-->
<template>
  <layout-teleport-to-breadcrumb>
    <layout-breadcrumb-loader
      :predicate="fetchState.status.value === 'success'"
    />
  </layout-teleport-to-breadcrumb>

  <transition
    v-if="fetchState.status.value === 'success'"
    appear
    appear-active-class="animate__animated animate__fadeIn ease-in-out a1"
  >
      <!-- TAB: CP/RTT -->
      <section
        :key="NAV_SECTION.CP_RTT"
        :class="[
          'page-parameters__tab',
          'page-parameters__tab--sheet'
        ]"
      >
        <ui-reset ref="resetOptionsForm">

          <leave-module-form
            class="page-parameters__leave-module-form"
            :default-data="leaveModuleFormData"
          />

          <acquiring-cycle-form
            class="page-parameters__acquiring-cycle-form"
            v-bind="{
              callback: acquisitionCycleCallback,
            }"
            :default-data="acquiringCycleFormData"
          />

          <options-form
            class="page-parameters__options-form"
            v-bind="{
              callback: optionsFormCallback,
              areRttAllowed: parameters.rtt_acquisition.allowed,
              areRcAllowed: parameters.rc_conversion.allowed,
              rcShortName: parameters.rc_conversion.short_name,
            }"
            :default-data="optionsFormData"
          />

        </ui-reset>
      </section>

  </transition>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables

.page-parameters

  &__leave-module-form,
  &__acquiring-cycle-form,
  &__options-form
    width: map-get($breakpoints, 'tablet')
    margin-bottom: $layout-spacer-y

</style>

<script lang="ts">
import LeaveModuleForm from '~/components/transat/parameters/LeaveModuleForm.vue'
import AcquiringCycleForm from '~/components/transat/parameters/AcquiringCycleForm.vue'
import OptionsForm from '~/components/transat/parameters/OptionsForm.vue'
import Console from '~/utils/lib/core/console'
import { isUndefined, noop, noop1, pipe_, renameKey_, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useStore_transat_parameters_module, type LeaveModuleInterface } from '~/stores/transat/parameters/module'
import { NAV_SECTION } from '../parameters.vue'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import type { FormData as AcquiringCycleFormData, SubmitData as AcquiringCycleSubmitdata } from '~/components/transat/parameters/AcquiringCycleForm.vue'
import type { FormData as LeaveModuleFormData } from '~/components/transat/parameters/LeaveModuleForm.vue'
import type { FormData as OptionsFormData, SubmitData as OptionsSubmitData } from '~/components/transat/parameters/OptionsForm.vue'
import type { UiReset } from '#components'
import { useAccessControl } from '~/composables/layout/access-control'

const console = new Console('component:leave-parameters')

export default defineNuxtComponent({
  components: { LeaveModuleForm, AcquiringCycleForm, OptionsForm },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()
    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const setNavSection = inject<((_: NAV_SECTION) => void)>('setNavSection', noop1)

    setNavSection(NAV_SECTION.CP_RTT)

    const ref_resetOptionsForm
      = useTemplateRef<InstanceType<typeof UiReset>>('resetOptionsForm')

    const store_parameters_module = useStore_transat_parameters_module()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ref_resetOptionsForm,
      store_parameters_module,
    }
  },
  data() {
    return {
      NAV_SECTION,
      parameters: null as unknown as LeaveModuleInterface, // lazy
      acquiringCycleFormData: null as unknown as AcquiringCycleFormData, // lazy
      leaveModuleFormData: null as unknown as LeaveModuleFormData, // lazy
      optionsFormData: null as unknown as OptionsFormData, // lazy
    }
  },
  created() {
    this.fetchWrapper(
      () => {
        const options = {
          uuidData: this.fromCompanyLevel().structure.uuidData,
          code_societe: this.fromCompanyLevel().company.code_societe,
        }

        return this.store_parameters_module.read(options)
          .then( this.parseModuleData )
          .catch(tapCatch(err => {
            console.warn('fetch', err)
            this.ToastBuilder
              .error(toString(err))
              .title(`Paramètres congés sociétés`)
              .show()
          }))
      }
    )
  },
  methods: {
    acquisitionCycleCallback(data :Partial<AcquiringCycleSubmitdata>) {
      const data_ = {
        cp_per_month: data.paidLeaveAcquisitionCount,
        rtt_allowed: data.areRttAllowed,
        rtt_per_month: data.rttAcquisitionCount,
        rc_allowed: data.areRcAllowed,
        rc_minutes_for_day: data.rcNumberOfMinutesForDay,
        rc_long_name: data.rcLongName,
        rc_short_name: data.rcShortName,
        remote_work_allowed: data.remoteWorkAllowed,
      }

      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        data: data_
      }

      return sustainedPromise(
          300,
          this.store_parameters_module.patchAcquiringCycle(options),
        )
        .then( this.parseModuleData )
        .then(() => {
          this.ref_resetOptionsForm?.reset()

          this.ToastBuilder
            .success()
            .title(`Paramètres du cycle d'acquisition modifiés`)
            .show()
        })
        .catch(err => {
          console.warn('acquisitionCycleCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification du cycle d'acquisition`)
            .show()
        })
    },
    optionsFormCallback(data: Partial<OptionsSubmitData>) {
      const data_ = pipe_(
        data,
        renameKey_('arePaidLeaveHalfDayAllowed', 'cp_allow_half_days'),
        renameKey_('areRttHalfDayAllowed', 'rtt_allow_half_days'),
        renameKey_('areRcHalfDayAllowed', 'rc_allow_half_days'),
        renameKey_('arePaidLeaveNegativeValueAllowed', 'cp_allow_negative_balance'),
        renameKey_('areRttNegativeValueAllowed', 'rtt_allow_negative_balance'),
        renameKey_('areRcNegativeValueAllowed', 'rc_allow_negative_balance'),
        renameKey_('paidLeaveNegativeValueLimitCount', 'cp_negative_balance_limit'),
        renameKey_('rttNegativeValueLimitCount', 'rtt_negative_balance_limit'),
        renameKey_('rcNegativeValueLimitCount', 'rc_negative_balance_limit'),
      )

      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        data: data_
      }

      return sustainedPromise(
          300,
          this.store_parameters_module.patchOptions(options)
        )
        .then( this.parseModuleData )
        .then(() => {
          this.ToastBuilder
            .success()
            .title(`Options modifiées`)
            .show()
        })
        .catch(err => {
          console.warn('optionsFormCallback')
          this.ToastBuilder
            .error(toString(err))
            .title(`Mis à jour des options`)
            .show()
        })
    },
    parseModuleData(data: LeaveModuleInterface) {
      this.parameters = data

      this.acquiringCycleFormData = {
        areRttAllowed: data.rtt_acquisition?.allowed || false,
        rttAcquisitionCount: toString(
          !isUndefined(data.rtt_acquisition?.days_per_month) ?
            data.rtt_acquisition?.days_per_month :
            ''
        ),
        paidLeaveAcquisitionCount: toString(data.cp_acquisition.days_per_month),
        areRcAllowed: data.rc_conversion?.allowed || false,
        rcLongName: data.rc_conversion?.long_name || '',
        rcShortName: data.rc_conversion?.short_name || '',
        rcNumberOfMinutesForDay: toString(
          !isUndefined(data.rc_conversion?.number_of_minutes_for_day) ?
            data.rc_conversion?.number_of_minutes_for_day :
            ''
        ),
        remoteWorkAllowed: data.remote_work_settings?.allowed || false,
      }

      this.leaveModuleFormData = {
        leaveCounting: data.base_leave_settings.workday_type,
        leaveYearStartingMonth: data.base_leave_settings.leave_year_start_month,
        withPaidLeaveCarryOver: data.base_leave_settings.carry_over_allowed
      }

      this.optionsFormData = {
        arePaidLeaveHalfDayAllowed: data.leave_options['paid-leave'].allow_half_days,
        areRttHalfDayAllowed: data.leave_options['rtt-paid-leave']?.allow_half_days || false,
        areRcHalfDayAllowed: data.leave_options['compensatory-leave']?.allow_half_days || false,
        areRcNegativeValueAllowed: data.leave_options['compensatory-leave']?.allow_negative_balance || false,
        arePaidLeaveNegativeValueAllowed: data.leave_options['paid-leave'].allow_negative_balance,
        areRttNegativeValueAllowed: data.leave_options['rtt-paid-leave']?.allow_negative_balance || false,
        paidLeaveNegativeValueLimitCount: toString(
          data.leave_options['paid-leave'].negative_balance_limit
        ),
        rttNegativeValueLimitCount: toString(
          !isUndefined(data.leave_options['rtt-paid-leave']?.negative_balance_limit) ?
            data.leave_options['rtt-paid-leave']?.negative_balance_limit :
            ''
        ),
        rcNegativeValueLimitCount: toString(
          !isUndefined(data.leave_options['compensatory-leave']?.negative_balance_limit) ?
            data.leave_options['compensatory-leave']?.negative_balance_limit :
            ''
        ),
      }
    },
  },
})
</script>
